<template>
  <CardSection :title="heading">
    <div v-if="!campaigns.length">{{ $t('No Campaigns') }}</div>
    <div v-for="camp in campaigns" :key="camp.name" class="campaign-list-item campaign"> 

      <div class="item-name-wrapper">
        <div class="campaign-name">{{ camp.name }}</div>
        <div class="item-date-wrapper-simple">
          {{ formatToTzSimple(camp.stDate, camp.timezone) }}
          <div v-if="!isDefaultEndDate(camp.endDate)" class="item-list-date-simple">
            &mdash;&nbsp;
            {{ formatToTzSimple(camp.endDate, camp.timezone) }}
          </div>
          <div class="item-date-simple-sep"></div>
        </div>
          <div v-if="camp.status === 'running' && (camp.hasPausedOffers || camp.hasPausedWidgets)" class="item-pause-warning">
            <Badge v-if="camp.hasPausedOffers" status="attention">{{$t('Offers Paused')}}</Badge>
            <Badge v-if="camp.hasPausedWidgets" status="attention">{{$t('Widgets Paused')}}</Badge>
          </div>
      </div><!-- item-name-wrapper -->

      <div class="CampaignActions">
        <ButtonGroup>

          <template v-if="camp.status === 'publishing'">
            <Button type="plain" :disabled="buttonsDisabled">{{ $t('Publishing') }}</Button>
          </template>
          <template v-else-if="camp.status === 'draft' && camp.creationStep !== -1">
            <Button type="plain" :disabled="buttonsDisabled" @click="edit(camp.id)">{{ $t('Edit') }}</Button>
          </template>
          <template v-else>
            <Button type="plain" :disabled="buttonsDisabled" @click="edit(camp.id)">{{ $t('Edit') }}</Button>
          </template>

          <ActionList :disabled="buttonsDisabled" :sections="campaignActions(camp)" placement="bottom-end">
            <Button type="plain" :disabled="buttonsDisabled" dropdown>{{ $t('Actions') }}</Button>
          </ActionList>
          
        </ButtonGroup>
      </div>

    </div>
    

  </CardSection>

</template>

<script>

import DateUtil from '../classes/utils/DateUtil'

export default {
  name: 'CampaignListBox',
  props: {
    heading: {
      type: String,
      required: true
    },
    campaigns: {
      type: Array,
      required: true
    },
    buttonsDisabled: {
      type: Boolean,
      required: true
    }
  },
  methods: {
    campaignActions(camp) {
      
      let items = [
        { label: this.$t('Notes'), onAction: () => this.eventHub.$emit('showCampaignNotes', camp.id) } //this.eventHub.$emit('showCampaignNotes', camp.id)
      ]

      if (camp.status === 'trashed') {
        items.push({ label: this.$t('Make Draft'), onAction: () => this.makeDraft(camp.id) })
      }
      if (camp.status !== 'trashed') {
        items.push({ label: this.$t('Duplicate'), onAction: () => this.duplicateCampaign(camp.id) })
      }
      if (camp.status === 'running') {
        items.push({ label: this.$t('End Campaign'), onAction: () => this.expireCampaign(camp.id) })
      }
      if (camp.status === 'draft' || camp.status === 'published') {
        items.push({ label: this.$t('Trash'), onAction: () => this.trashCampaign(camp.id) })
      }
      if (camp.status === 'running') {
        if (!camp.hasPausedOffers) {
          items.push({ label: this.$t('Pause Offers'), onAction: () => this.pauseOffers(camp.id) })
        } else {
          items.push({ label: this.$t('Unpause Offers'), onAction: () => this.unpauseOffers(camp.id) })
        }
        if (!camp.hasPausedWidgets) {
          items.push({ label: this.$t('Pause Widgets'), onAction: () => this.pauseWidgets(camp.id) })
        } else {
          items.push({ label: this.$t('Unpause Widgets'), onAction: () => this.unpauseWidgets(camp.id) })
        }
      } 
      if (camp.status === 'running' || camp.status === 'expired') {
        items.push({ label: this.$t('Download Emails'), onAction: () => { this.$router.push(`/account/exportEmails/?campaignId=${camp.id}`) } })
      }

      // if (this.theUser.isSuper) {
      //   items.push({ label: this.$t('Segment Actions'), onAction: () => this.segmentActions(camp.id) })
      // }

      let actions = [{ items }]

      return actions
    },
    segmentActions(campaignId) {
      this.$router.push(`/campaigns/${campaignId}/segmentActions/`)
    },
    edit(id) {
      this.$emit('edit', id)
    },
    formatToTzSimple(endDate, timezone) {
      return DateUtil.formatToTzSimple(endDate, timezone)
    },
    isDefaultEndDate(date) {
      if (!date) return true //?
      return DateUtil.isDefaultEndDate(date)
    },
    alert(message) {
      this.$ui.alert({
        title: this.$t('Alert'),
        content: message
      })
    },
    validateName(newName) {
      if (newName && newName.trim()) {
        const sameNameCampaign = this.campaigns.filter(camp => {
          return camp.name === newName.trim()
        })
        if (!sameNameCampaign.length) {
          return true
        }
      }
      return false
    },
    async duplicateCampaign(campaignId) {
      const newName = await this.$ui.prompt({
        title: this.$t('Duplicate Campaign'),
        content: this.$t('Enter New Campaign Name'),
        primaryActionLabel: this.$t('Create'),
        inputPlaceholder: this.$t('Campaign Name')
      })
      if (this.validateName(newName)) {
        // console.log(newName)
        NProgress.start()
        this.$emit('set-saving', true)
        this.api.startCopyCampaign(campaignId, newName)
          .then(() => {
            this.eventHub.$emit('startBackgroundProcess')
          })
          .catch(this.apiCatch)
          .finally(() => {
            this.$emit('set-saving', false)
            NProgress.done()
          })     
      }
    },
    makeDraft(campaignId) {
      NProgress.start()
      this.$emit('set-saving', true)
      this.api.makeDraftCampaign(campaignId)
        .then(() => {
          const camp = this.campaigns.find(c => c.id === campaignId)
          camp.status = 'draft'
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.$emit('set-saving', false)
          NProgress.done()
        })
    },
    expireCampaign(campaignId) {
      NProgress.start()
      this.$emit('set-saving', true)
      this.api.expireCampaign(campaignId)
        .then(() => {
          const camp = this.campaigns.find(c => c.id === campaignId)
          camp.status = 'expired'
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.$emit('set-saving', false)
          NProgress.done()
        })
    },
    trashCampaign(campaignId) {
      NProgress.start()
      this.$emit('set-saving', true)
      this.api.trashCampaign(campaignId)
        .then(() => {
          const camp = this.campaigns.find(c => c.id === campaignId)
          camp.status = 'trashed'
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.$emit('set-saving', false)
          NProgress.done()
        })
    },
    pauseOffers(campaignId) {
      NProgress.start()
      this.$emit('set-saving', true)
      this.api.pauseCampaignOffers(campaignId)
        .then(() => {
          const camp = this.campaigns.find(c => c.id === campaignId)
          camp.hasPausedOffers = true
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.$emit('set-saving', false)
          NProgress.done()
        })
    },
    unpauseOffers(campaignId) {
      NProgress.start()
      this.$emit('set-saving', true)
      this.api.unpauseCampaignOffers(campaignId)
        .then(() => {
          const camp = this.campaigns.find(c => c.id === campaignId)
          camp.hasPausedOffers = false
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.$emit('set-saving', false)
          NProgress.done()
        })
    },
    pauseWidgets(campaignId) {
      NProgress.start()
      this.$emit('set-saving', true)
      this.api.pauseCampaignWidgets(campaignId)
        .then(() => {
          const camp = this.campaigns.find(c => c.id === campaignId)
          camp.hasPausedWidgets = true          
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.$emit('set-saving', false)
          NProgress.done()
        })
    },
    unpauseWidgets(campaignId) {
      NProgress.start()
      this.$emit('set-saving', true)
      this.api.unpauseCampaignWidgets(campaignId)
        .then(() => {
          const camp = this.campaigns.find(c => c.id === campaignId)
          camp.hasPausedWidgets = false
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.$emit('set-saving', false)
          NProgress.done()
        })
    }
  }
}
</script>

<style lang="scss" scoped>

.CampaignActions {
  display: flex;
  &>div {
    margin-left: auto;
  }
}

.label {
  display: inline;
  padding: .2em .6em .3em;
  font-size: 75%;
  font-weight: 700;
  line-height: 1;
  color: #fff;
  text-align: center;
  white-space: nowrap;
  vertical-align: baseline;
  border-radius: .25em;
}
.label-warning {
  background-color: #f0ad4e;
}

.campaign-list-item {
  border-bottom: 1px solid #e5e5e5;
  margin-bottom: 15px;
  padding-bottom: 15px;
  position: relative;

  &:last-of-type {
    border-bottom: 0;
    margin-bottom: 0;
    padding-bottom: 0;
  }
  .item-name-wrapper {
    .campaign-name {
      font-size: 120%;
      display: inline-block;
      margin-right: 10px;
    }
    .item-date-wrapper-simple {
      font-size: 90%;
      display: inline-block;
    }
    .item-list-date-simple {
      display: inline-block;
    }
    .item-pause-warning {
      .label {
        margin-right: 5px;
      }
    }
  }
  .campaign-actions {
    position: absolute;
    right: 0;
    top: 0;
    ul {
      list-style-type: none;
      padding: 0;
      margin: 0;
      margin-top: 15px;
      padding-top: 7px;
      padding-bottom: 7px;
      li {
        display: inline-block;
        a {
          i {
            display: inline-block;
            margin-right: 5px;
          }
        }
      }
      &.actions-no-btn {
        li {
          a {
            background: transparent;
            border: 0;
          }
        }
      }
    }

    &> button {
      padding: 0px;
      height: 53px;
      a {
        padding: 6px 12px;
        position: relative;
        top: 6px;
      }
    }

    .caret {
      margin-left: 5px;
    }

    .dropdown {
      position: absolute;
      right: -10px;
      top: 67px;
      width: 300px;

      .divider {
        height: 1px;
        margin: 9px 0;
        overflow: hidden;
        background-color: #e5e5e5;
      }

      .head {
        a {
          color: #000;
          span {
            display: block;
            color: #ccc;
            font-size: .7em;
          }
        }
        margin-bottom: 10px;
      }

      @media screen and (max-height: 900px) {
        margin-right: 1px;
      }
      a {
        cursor: pointer;
      }
      button {
        @media screen and (max-height: 900px) {
          padding: 0px 12px;
        }
      }
      .user {
        color: #888;
        display: flex;
        padding-right: 20px;
        .caret {
          margin-top: 15px;
          display: inline-block;
          margin-right: 10px;
          @media screen and (max-height: 900px) {
            margin-top: 11px;
          }
        }
        .fa {
          font-size: 32px;
          @media screen and (max-height: 900px) {
            font-size: 20px;
            padding-top: 2px;
          }
        }
        .account {
          padding-left: .5em;
          font-size: 22px;
          font-weight: 100;
          @media screen and (max-height: 900px) {
            font-size: 18px;
          }
        }
      }

      a.selected {
        font-weight: bold;
      }

      &.dropdown-menu {
        top: 0;
        left: auto;
        margin-top: -1px;
        box-shadow: 0 6px 12px rgba(0,0,0,.175);
        top: 9px !important;
        left: 7px !important;

        li a {
          display: block;
          padding: 3px 20px;
          clear: both;
          font-weight: 400;
          line-height: 1.42857143;
          color: #333;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;

          &:hover {
            text-decoration: none;
            background-color: #f5f5f5;
          }
        }
      }
    }

    .dropdown-toggle {
      padding-left: 10px;
    }
    .dropdown-menu {
      background: #fff;
      li, a {
        width: 100%;
      }
    }

  }
}
</style>